import { useMemo, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { TrashIcon } from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DeleteModal } from 'src/common/DeleteModal';
import { useAssignmentsBulkDeletePartialUpdateMutation } from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';

import { setSelectedAssignmentRows } from './assignmentManagement.slice';
import {
  ErrorResponse,
  formatAssignmentDeleteErrors,
} from './formatAssignmentDeleteErrors';

export const AssignmentManagementBulkDeleteButton = () => {
  const dispatch = useAppDispatch();

  const [bulkDelete] = useAssignmentsBulkDeletePartialUpdateMutation();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | string[] | undefined>(undefined);

  const selectedAssignmentRows = useAppSelector(
    (state) => state.assignmentManagement.selectedAssignmentRows
  );

  const selectedAssignmentRowsList = useMemo(() => {
    return Object.keys(selectedAssignmentRows).filter(
      (id) => !!selectedAssignmentRows[id]
    );
  }, [selectedAssignmentRows]);

  if (!selectedAssignmentRowsList.length) {
    return null;
  }

  const handleDeleteAssignments = async () => {
    setIsLoading(true);
    try {
      await bulkDelete({
        patchedBulkAssignmentDelete: {
          assignment_ids: selectedAssignmentRowsList,
        },
      }).unwrap();
      setShowDeleteModal(false);

      dispatch(
        show({
          title: `Success`,
          description: `Deleted (${selectedAssignmentRowsList.length}) assignments`,
          type: 'success',
        })
      );

      dispatch(setSelectedAssignmentRows({}));
    } catch (error) {
      setError(formatAssignmentDeleteErrors(error as ErrorResponse));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="danger"
        onClick={() => setShowDeleteModal(true)}
        dataTestId="bulk-delete-assignments-button"
      >
        <TrashIcon className="mr-2 h-5 w-5" /> Delete (
        {`${selectedAssignmentRowsList.length}`}) Crew Schedule(s)
      </Button>
      <DeleteModal
        open={showDeleteModal}
        setOpen={(value) => {
          setError('');
          setShowDeleteModal(value);
        }}
        onCancel={() => {
          setError('');
          setShowDeleteModal(false);
        }}
        onConfirm={handleDeleteAssignments}
        title={`Delete ${selectedAssignmentRowsList.length} crew schedule(s)?`}
        description={`Are you sure you want to delete ${selectedAssignmentRowsList.length} 
          crew schedule(s)? This action cannot be undone.`}
        loading={isLoading}
        error={error}
      />
    </>
  );
};
